import React, { useState } from 'react'
import { Link } from '@inertiajs/react'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import OperatorLayout from '@/layouts/OperatorLayout'
import PricingRulesNav from '@/components/pricing-rules/PricingRulesNav'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Plus, Flag, X, Pencil, Check, Link2, Link2Off, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import RampPreviewChart from '@/components/pricing-rules/RampPreviewChart'
import EventTimelineChart from '@/components/pricing-rules/EventTimelineChart'
import type { SharedProps } from '@/types/shared'

interface PropertyEventItem {
  id: number
  eventId: number
  eventName: string
  startDate: string
  endDate: string
  impactLevel: string
  enabled: boolean
  demandMultiplier: number | null
  effectiveMultiplier: number
  notes: string | null
  rampUpDays: number | null
  rampDownDays: number | null
  rampCurve: string | null
  effectiveRampUpDays: number
  effectiveRampDownDays: number
  effectiveRampCurve: 'linear' | 'ease_in'
}

interface AvailableEvent {
  id: number
  name: string
  startDate: string
  endDate: string
  impactLevel: string
  demandMultiplier: number
  notes: string | null
  rampUpDays: number
  rampDownDays: number
  rampCurve: string
}

interface EventsProps extends SharedProps {
  propertyEvents: PropertyEventItem[]
  availableEvents: AvailableEvent[]
}

const IMPACT_COLORS: Record<string, string> = {
  minor: 'bg-slate-100 text-slate-700',
  moderate: 'bg-amber-100 text-amber-700',
  major: 'bg-orange-100 text-orange-700',
  extreme: 'bg-rose-100 text-rose-700',
}

const IMPACT_BAR_COLORS: Record<string, string> = {
  minor: 'bg-slate-300',
  moderate: 'bg-amber-400',
  major: 'bg-orange-500',
  extreme: 'bg-rose-500',
}

function formatDateRange(start: string, end: string): string {
  const s = new Date(start + 'T00:00:00')
  const e = new Date(end + 'T00:00:00')
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }
  return `${s.toLocaleDateString('en-GB', opts)} - ${e.toLocaleDateString('en-GB', opts)}`
}

function multiplierToPercent(m: number): string {
  const pct = (m - 1) * 100
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`
}

function hasRampOverride(pe: PropertyEventItem): boolean {
  return pe.rampUpDays !== null || pe.rampDownDays !== null || pe.rampCurve !== null
}

export default function Events({
  currentUser,
  property,
  properties,
  propertyEvents,
  availableEvents,
}: EventsProps) {
  const base = property.basePath
  const [assignOpen, setAssignOpen] = useState(false)
  const [overrideMultiplier, setOverrideMultiplier] = useState('')
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)

  // Inline multiplier editing
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editValue, setEditValue] = useState('')

  // Collapsible row expansion
  const [expandedId, setExpandedId] = useState<number | null>(null)

  // Local ramp editing state
  const [rampLinked, setRampLinked] = useState(true)
  const [localRampUp, setLocalRampUp] = useState(0)
  const [localRampDown, setLocalRampDown] = useState(0)
  const [localCurve, setLocalCurve] = useState<'linear' | 'ease_in'>('linear')
  const [rampSaving, setRampSaving] = useState(false)

  // Every change on this tab goes through the server review first (change safety R1.5): the review
  // posts the save's own fields plus the operation, and the confirm sends the same request as before.
  const review = usePricingReview()
  const reviewEndpoint = `${base}/pricing-rules/events/review`

  function reviewUpdate(pe: PropertyEventItem, fields: Record<string, unknown>, options: Parameters<typeof inertiaSave>[3] = {}) {
    review.open(reviewEndpoint, { operation: 'update', property_event_id: pe.id, ...fields }, () =>
      inertiaSave('patch', `${base}/pricing-rules/events/${pe.id}`, fields, options)
    )
  }

  const selectedEvent = availableEvents.find((e) => e.id === selectedEventId) ?? null

  // The assign dialog steps aside for the review and comes back, choice intact, on "Back".
  function handleAssign(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedEventId) return
    const eventId = selectedEventId
    const data: Record<string, string> = {}
    if (overrideMultiplier) data.override_multiplier = overrideMultiplier
    setAssignOpen(false)
    review.open(
      reviewEndpoint,
      { operation: 'assign', event_id: eventId, ...data },
      () =>
        inertiaSave('post', `${base}/pricing-rules/events/${eventId}/assign`, data, {
          onStart: () => setSaving(true),
          onFinish: () => {
            setSaving(false)
            setOverrideMultiplier('')
            setSelectedEventId(null)
          },
        }),
      { onBack: () => setAssignOpen(true) }
    )
  }

  // Replaces the browser's confirm(): the review names the event, its dates and the hotel.
  function handleUnassign(propertyEventId: number) {
    review.open(reviewEndpoint, { operation: 'unassign', property_event_id: propertyEventId }, () =>
      inertiaSave('delete', `${base}/pricing-rules/events/${propertyEventId}`)
    )
  }

  // The switch shows the stored state, so a cancelled review leaves it where it was.
  function handleToggleEnabled(pe: PropertyEventItem) {
    reviewUpdate(pe, { enabled: !pe.enabled })
  }

  function startEditing(pe: PropertyEventItem) {
    setEditingId(pe.id)
    setEditValue(pe.demandMultiplier != null ? String(pe.demandMultiplier) : String(pe.effectiveMultiplier))
  }

  function saveMultiplier(pe: PropertyEventItem) {
    const val = parseFloat(editValue)
    if (isNaN(val) || val < 0.5 || val > 5) return
    // A cancelled review leaves the inline editor open with the value typed.
    reviewUpdate(pe, { demand_multiplier: val }, { onFinish: () => setEditingId(null) })
  }

  function cancelEditing() {
    setEditingId(null)
    setEditValue('')
  }

  function expandRow(pe: PropertyEventItem) {
    if (expandedId === pe.id) {
      setExpandedId(null)
      return
    }
    setExpandedId(pe.id)
    setLocalRampUp(pe.effectiveRampUpDays)
    setLocalRampDown(pe.effectiveRampDownDays)
    setLocalCurve(pe.effectiveRampCurve)
    setRampLinked(pe.effectiveRampUpDays === pe.effectiveRampDownDays)
  }

  function handleRampUpChange(value: number) {
    const clamped = Math.max(0, Math.min(14, value))
    setLocalRampUp(clamped)
    if (rampLinked) setLocalRampDown(clamped)
  }

  function handleRampDownChange(value: number) {
    const clamped = Math.max(0, Math.min(14, value))
    setLocalRampDown(clamped)
    if (rampLinked) setLocalRampUp(clamped)
  }

  function saveRamp(pe: PropertyEventItem) {
    reviewUpdate(
      pe,
      { ramp_up_days: localRampUp, ramp_down_days: localRampDown, ramp_curve: localCurve },
      {
        onStart: () => setRampSaving(true),
        onFinish: () => {
          setRampSaving(false)
          setExpandedId(null)
        },
      }
    )
  }

  function cancelRamp() {
    setExpandedId(null)
  }

  // Map propertyEvents to EventTimelineChart format
  const timelineEvents = propertyEvents.map((pe) => ({
    eventName: pe.eventName,
    startDate: pe.startDate,
    endDate: pe.endDate,
    effectiveMultiplier: pe.effectiveMultiplier,
    effectiveRampUpDays: pe.effectiveRampUpDays,
    effectiveRampDownDays: pe.effectiveRampDownDays,
    effectiveRampCurve: pe.effectiveRampCurve,
    enabled: pe.enabled,
  }))

  return (
    <OperatorLayout title="Events" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <PricingRulesNav
          propertyBasePath={property.basePath}
          activeTab="events"
          help={{
            content:
              'Events boost your rates during high-demand periods like festivals, conferences, and local happenings. Rates can ramp up gradually before the event and ease down after -- or jump immediately if you prefer. When events overlap, the highest boost wins.',
          }}
        />

        {/* Event Timeline Chart (hero visualization) */}
        {propertyEvents.length > 0 && (
          <div className="animate-slide-in" style={{ animationDelay: '50ms' }}>
            <EventTimelineChart events={timelineEvents} />
          </div>
        )}

        <div className="animate-slide-in space-y-6" style={{ animationDelay: '100ms' }}>
          {/* Section header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground">Property Events</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Events assigned to this hotel that affect rate computation.
                {' '}
                <Link href={`${base}/events`} className="text-primary hover:underline">
                  Manage event catalogue
                </Link>
              </p>
            </div>
            {availableEvents.length > 0 && (
              <Button onClick={() => setAssignOpen(true)} className="rounded-lg" size="sm">
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Assign Event
              </Button>
            )}
          </div>

          {/* Assigned events table */}
          {propertyEvents.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <Flag className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No events assigned</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Assign events to automatically adjust rates during event periods.
              </p>
              {availableEvents.length > 0 && (
                <Button
                  onClick={() => setAssignOpen(true)}
                  variant="outline"
                  size="sm"
                  className="mt-4 rounded-lg"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Assign First Event
                </Button>
              )}
            </div>
          ) : (
            <div className="rounded-xl border border-border/60 bg-white shadow-sm overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Impact</TableHead>
                    <TableHead>
                      <span className="inline-flex items-center gap-1">
                        Multiplier
                        <HelpTooltip
                          content="The rate multiplier during this event. 1.20x means rates are 20% higher. Click the pencil to set a custom override for this property."
                        />
                      </span>
                    </TableHead>
                    <TableHead className="text-center">Enabled</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {propertyEvents.map((pe) => {
                    const isEditing = editingId === pe.id
                    const isExpanded = expandedId === pe.id
                    const hasRamp = pe.effectiveRampUpDays > 0 || pe.effectiveRampDownDays > 0
                    return (
                      <React.Fragment key={pe.id}>
                        {/* Main row */}
                        <TableRow
                          className={cn(
                            !pe.enabled && 'opacity-50',
                            isExpanded && 'border-b-0',
                            'cursor-pointer'
                          )}
                          onClick={() => expandRow(pe)}
                        >
                          <TableCell className="w-8 pr-0">
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground">
                              {isExpanded ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <span className="font-medium">{pe.eventName}</span>
                            {pe.notes && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[200px]">{pe.notes}</p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">
                                {formatDateRange(pe.startDate, pe.endDate)}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {hasRamp
                                  ? `${pe.effectiveRampUpDays}d | peak | ${pe.effectiveRampDownDays}d`
                                  : 'peak'}
                              </span>
                              {hasRampOverride(pe) && (
                                <Badge variant="secondary" className="text-[10px] bg-primary/10 text-primary">
                                  override
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary" className={cn('text-[11px] capitalize', IMPACT_COLORS[pe.impactLevel])}>
                                {pe.impactLevel}
                              </Badge>
                              {/* Impact bar */}
                              <div className="hidden sm:flex items-center gap-0.5">
                                {['minor', 'moderate', 'major', 'extreme'].map((level) => (
                                  <div
                                    key={level}
                                    className={cn(
                                      'h-1.5 w-3 rounded-sm',
                                      ['minor', 'moderate', 'major', 'extreme'].indexOf(pe.impactLevel) >= ['minor', 'moderate', 'major', 'extreme'].indexOf(level)
                                        ? IMPACT_BAR_COLORS[level]
                                        : 'bg-muted/50'
                                    )}
                                  />
                                ))}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            {isEditing ? (
                              <div className="flex items-center gap-1.5">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0.5"
                                  max="5"
                                  className="w-20 text-center rounded-lg text-sm font-semibold h-8"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); saveMultiplier(pe) }
                                    if (e.key === 'Escape') cancelEditing()
                                  }}
                                  autoFocus
                                />
                                <span className="text-xs text-muted-foreground">x</span>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-primary" onClick={() => saveMultiplier(pe)}>
                                  <Check className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground" onClick={cancelEditing}>
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="tabular-nums font-semibold">{pe.effectiveMultiplier.toFixed(2)}x</span>
                                <span className={cn(
                                  'text-xs tabular-nums',
                                  pe.effectiveMultiplier > 1 ? 'text-emerald-600' : pe.effectiveMultiplier < 1 ? 'text-rose-600' : 'text-muted-foreground'
                                )}>
                                  ({multiplierToPercent(pe.effectiveMultiplier)})
                                </span>
                                {pe.demandMultiplier != null && (
                                  <span className="text-[10px] font-normal text-muted-foreground">(override)</span>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                                  onClick={() => startEditing(pe)}
                                >
                                  <Pencil className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                            <Switch
                              checked={pe.enabled}
                              onCheckedChange={() => handleToggleEnabled(pe)}
                              aria-label={`${pe.eventName} in use`}
                            />
                          </TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 rounded-lg p-0 text-rose-500 hover:text-rose-600"
                              onClick={() => handleUnassign(pe.id)}
                              aria-label={`Remove ${pe.eventName}`}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>

                        {/* Expanded ramp settings row */}
                        {isExpanded && (
                          <TableRow className="bg-muted/20 hover:bg-muted/20">
                            <TableCell colSpan={7} className="p-0">
                              <div className="px-6 py-5 space-y-5" onClick={(e) => e.stopPropagation()}>
                                {/* Ramp settings header */}
                                <div className="flex items-center gap-2">
                                  <h3 className="text-sm font-semibold text-foreground">Ramp Settings</h3>
                                  {hasRampOverride(pe) && (
                                    <Badge variant="secondary" className="text-[10px] bg-primary/10 text-primary">
                                      Property override
                                    </Badge>
                                  )}
                                </div>

                                <div className="flex flex-col lg:flex-row gap-6">
                                  {/* Ramp controls */}
                                  <div className="space-y-4 flex-shrink-0">
                                    {/* Before / Link / After row */}
                                    <div className="flex items-end gap-3">
                                      {/* Before event */}
                                      <div className="space-y-1.5">
                                        <Label className="text-xs text-muted-foreground">Before event</Label>
                                        <div className="flex items-center gap-1">
                                          <Input
                                            type="number"
                                            min={0}
                                            max={14}
                                            step={1}
                                            className="w-16 text-center rounded-lg text-sm h-9"
                                            value={localRampUp}
                                            onChange={(e) => handleRampUpChange(parseInt(e.target.value) || 0)}
                                          />
                                          <span className="text-xs text-muted-foreground">days</span>
                                        </div>
                                      </div>

                                      {/* Link toggle */}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={cn(
                                          'h-9 w-9 p-0 mb-0',
                                          rampLinked ? 'text-primary' : 'text-muted-foreground'
                                        )}
                                        onClick={() => setRampLinked(!rampLinked)}
                                        title={rampLinked ? 'Unlink before/after days' : 'Link before/after days'}
                                      >
                                        {rampLinked ? (
                                          <Link2 className="h-4 w-4" />
                                        ) : (
                                          <Link2Off className="h-4 w-4" />
                                        )}
                                      </Button>

                                      {/* After event */}
                                      <div className="space-y-1.5">
                                        <Label className="text-xs text-muted-foreground">After event</Label>
                                        <div className="flex items-center gap-1">
                                          <Input
                                            type="number"
                                            min={0}
                                            max={14}
                                            step={1}
                                            className="w-16 text-center rounded-lg text-sm h-9"
                                            value={localRampDown}
                                            onChange={(e) => handleRampDownChange(parseInt(e.target.value) || 0)}
                                          />
                                          <span className="text-xs text-muted-foreground">days</span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Curve select */}
                                    <div className="space-y-1.5">
                                      <Label className="text-xs text-muted-foreground">Curve</Label>
                                      <Select
                                        value={localCurve}
                                        onValueChange={(v) => setLocalCurve(v as 'linear' | 'ease_in')}
                                      >
                                        <SelectTrigger className="w-40 h-9 text-sm">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="linear">Linear</SelectItem>
                                          <SelectItem value="ease_in">Ease-in</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>

                                    {/* UI tip for long ramps */}
                                    {(localRampUp > 7 || localRampDown > 7) && (
                                      <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
                                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                                        <p className="text-xs text-amber-700">
                                          Long ramp periods (&gt;7 days) may overlap with other events or reduce the impact of this event's peak rate. Consider shorter ramps for more concentrated rate boosts.
                                        </p>
                                      </div>
                                    )}

                                    {/* Save / Cancel */}
                                    <div className="flex items-center gap-2 pt-1">
                                      <Button
                                        size="sm"
                                        className="rounded-lg"
                                        onClick={() => saveRamp(pe)}
                                        disabled={rampSaving}
                                      >
                                        {rampSaving ? 'Saving...' : 'Save Ramp'}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="rounded-lg"
                                        onClick={cancelRamp}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Ramp preview chart */}
                                  <div className="flex-1 min-w-0">
                                    {(localRampUp > 0 || localRampDown > 0) ? (
                                      <div className="rounded-lg border border-border/40 bg-white p-3">
                                        <p className="text-xs font-medium text-muted-foreground mb-2">Ramp Preview</p>
                                        <RampPreviewChart
                                          startDate={pe.startDate}
                                          endDate={pe.endDate}
                                          rampUpDays={localRampUp}
                                          rampDownDays={localRampDown}
                                          rampCurve={localCurve}
                                          peakMultiplier={pe.effectiveMultiplier}
                                        />
                                      </div>
                                    ) : (
                                      <div className="rounded-lg border border-dashed border-border/40 bg-muted/10 p-8 text-center">
                                        <p className="text-xs text-muted-foreground">
                                          Set ramp days above 0 to see a preview of how the multiplier ramps up and down around the event.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Assign Event Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Assign Event to {property.name}</DialogTitle>
            <DialogDescription>
              The engine prices the event into this hotel&apos;s nights. You see the change before it is saved.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssign} className="flex min-h-0 flex-1 flex-col">
            <DialogBody className="space-y-4">
              <div className="space-y-2">
                <Label>Select Event</Label>
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {availableEvents.map((event) => (
                    <button
                      key={event.id}
                      type="button"
                      onClick={() => setSelectedEventId(event.id)}
                      className={cn(
                        'w-full text-left rounded-lg border p-3 transition-all cursor-pointer',
                        selectedEventId === event.id
                          ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                          : 'border-border/60 hover:border-border hover:bg-muted/30'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{event.name}</span>
                        <Badge variant="secondary" className={cn('text-[10px] capitalize', IMPACT_COLORS[event.impactLevel])}>
                          {event.impactLevel}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatDateRange(event.startDate, event.endDate)} &middot; {event.demandMultiplier.toFixed(2)}x
                      </p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="override-mult" className="inline-flex items-center gap-1">
                  Override Multiplier
                  <HelpTooltip
                    content="Optional. Set a custom multiplier for this property instead of using the event's default. For example, 1.30x means rates will be 30% higher during the event."
                  />
                </Label>
                <Input
                  id="override-mult"
                  type="number"
                  step="0.01"
                  min="0.5"
                  max="5"
                  placeholder="Leave empty to use event default"
                  value={overrideMultiplier}
                  onChange={(e) => setOverrideMultiplier(e.target.value)}
                />
              </div>
              {selectedEvent && (
                <p className="text-xs text-muted-foreground">
                  Ramp: {selectedEvent.rampUpDays} day{selectedEvent.rampUpDays !== 1 ? 's' : ''} before,{' '}
                  {selectedEvent.rampDownDays} day{selectedEvent.rampDownDays !== 1 ? 's' : ''} after ({selectedEvent.rampCurve === 'ease_in' ? 'ease-in' : 'linear'})
                </p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-lg" onClick={() => setAssignOpen(false)}>Cancel</Button>
              <Button type="submit" className="rounded-lg" disabled={!selectedEventId || saving}>
                {saving ? 'Assigning...' : 'Assign Event'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {review.dialog}
    </OperatorLayout>
  )
}
